// src/components/AdminWallet.jsx
import React, { useState, useEffect } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import './Wallet.css';

const AdminWallet = () => {
    const { getWalletSummary, adminAdjustBalance, walletAccounts } = useWallet();
    const { showToast } = useToast();
    const [wallets, setWallets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustReason, setAdjustReason] = useState('');
    const [adjustAccount, setAdjustAccount] = useState('main');
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadWalletSummary();
    }, []);

    const loadWalletSummary = async () => {
        setLoading(true);
        const data = await getWalletSummary();
        if (data) {
            setWallets(data.wallets);
            setSummary(data);
        }
        setLoading(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount || 0);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString();
    };

    const handleAdjustBalance = async () => {
        if (!adjustAmount || parseFloat(adjustAmount) === 0) {
            showToast('Please enter an amount', 'error');
            return;
        }

        const result = await adminAdjustBalance(
            selectedWallet.userId,
            parseFloat(adjustAmount),
            adjustAccount,
            adjustReason
        );

        if (result.success) {
            setShowAdjustModal(false);
            setAdjustAmount('');
            setAdjustReason('');
            loadWalletSummary();
        }
    };

    const filteredWallets = wallets.filter(wallet =>
        wallet.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wallet.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalBalance = wallets.reduce((sum, w) => sum + (w.balance || 0), 0);

    return (
        <div className="admin-wallet">
            <div className="admin-wallet-header">
                <h2>Wallet Administration</h2>
                <button onClick={loadWalletSummary} className="btn-refresh">
                    <i className="fas fa-sync-alt"></i> Refresh
                </button>
            </div>

            {/* Summary Cards */}
            <div className="summary-cards">
                <div className="summary-card">
                    <div className="summary-label">Total Wallets</div>
                    <div className="summary-value">{wallets.length}</div>
                </div>
                <div className="summary-card">
                    <div className="summary-label">Total Balance</div>
                    <div className="summary-value">{formatCurrency(totalBalance)}</div>
                </div>
                {summary?.accountTotals && (
                    <>
                        {Object.entries(summary.accountTotals).map(([key, value]) => (
                            <div key={key} className="summary-card">
                                <div className="summary-label">{walletAccounts.find(a => a.id === key)?.name || key}</div>
                                <div className="summary-value">{formatCurrency(value)}</div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            {/* Search */}
            <div className="search-section">
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>

            {/* Wallets Table */}
            <div className="wallets-table-container">
                <table className="admin-wallet-table">
                    <thead>
                        <tr>
                            <th>User Name</th>
                            <th>Email</th>
                            <th>Total Balance</th>
                            {walletAccounts.map(account => (
                                <th key={account.id}>{account.name}</th>
                            ))}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={walletAccounts.length + 4} className="loading-cell">
                                    <i className="fas fa-spinner fa-spin"></i> Loading...
                                </td>
                            </tr>
                        ) : filteredWallets.length === 0 ? (
                            <tr>
                                <td colSpan={walletAccounts.length + 4} className="empty-cell">
                                    No wallets found
                                </td>
                            </tr>
                        ) : (
                            filteredWallets.map(wallet => (
                                <tr key={wallet.userId}>
                                    <td>{wallet.userName}</td>
                                    <td>{wallet.email}</td>
                                    <td className="balance-cell">{formatCurrency(wallet.balance)}</td>
                                    {walletAccounts.map(account => (
                                        <td key={account.id}>{formatCurrency(wallet.accounts?.[account.id] || 0)}</td>
                                    ))}
                                    <td>
                                        <button 
                                            className="btn-adjust"
                                            onClick={() => {
                                                setSelectedWallet(wallet);
                                                setShowAdjustModal(true);
                                            }}
                                        >
                                            <i className="fas fa-edit"></i> Adjust
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Adjust Balance Modal */}
            {showAdjustModal && (
                <div className="modal-overlay" onClick={() => setShowAdjustModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Adjust Wallet Balance</h3>
                            <button className="modal-close" onClick={() => setShowAdjustModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p><strong>User:</strong> {selectedWallet?.userName}</p>
                            <p><strong>Current Balance:</strong> {formatCurrency(selectedWallet?.balance)}</p>
                            
                            <div className="form-group">
                                <label>Account</label>
                                <select 
                                    value={adjustAccount} 
                                    onChange={(e) => setAdjustAccount(e.target.value)}
                                    className="form-control"
                                >
                                    {walletAccounts.map(account => (
                                        <option key={account.id} value={account.id}>{account.name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>Amount (KES)</label>
                                <input 
                                    type="number" 
                                    value={adjustAmount}
                                    onChange={(e) => setAdjustAmount(e.target.value)}
                                    placeholder="Enter amount (positive or negative)"
                                    className="form-control"
                                />
                                <small>Positive amounts add funds, negative amounts deduct</small>
                            </div>
                            
                            <div className="form-group">
                                <label>Reason</label>
                                <textarea 
                                    value={adjustReason}
                                    onChange={(e) => setAdjustReason(e.target.value)}
                                    placeholder="Reason for adjustment..."
                                    className="form-control"
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowAdjustModal(false)}>Cancel</button>
                            <button className="btn-submit" onClick={handleAdjustBalance}>Apply Adjustment</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminWallet;
